import { useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface NavItem {
  label: string;
  href: string;
  submenu?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    label: 'INCHIOSTRI',
    href: '/categoria/inchiostri',
    submenu: [
      { label: 'DTF', href: '/categoria/dtf' },
      { label: 'DTF UV', href: '/categoria/dtf-uv' },
      { label: 'DTF UV SOFT', href: '/categoria/dtf-uv-soft' },
    ],
  },
  {
    label: 'BOBINE',
    href: '/categoria/bobine',
    submenu: [
      { label: 'BOBINE DTF', href: '/categoria/bobine-dtf' },
      { label: 'BOBINE DTF UV', href: '/categoria/bobine-dtf-uv' },
      { label: 'BOBINE SPECIALI', href: '/categoria/bobine-speciali' },
    ],
  },
  {
    label: 'COLLA',
    href: '/categoria/colla',
    submenu: [
      { label: 'COLLA DTF', href: '/categoria/colla-dtf' },
    ],
  },
  {
    label: 'RICAMBI',
    href: '/ricambi',
    submenu: [
      { label: 'TESTINE', href: '/categoria/testine' },
      { label: 'COMPONENTI', href: '/categoria/componenti' },
    ],
  },
  {
    label: 'ASSISTENZA',
    href: '/assistenza',
  },
  {
    label: 'PROFILATURA',
    href: '/profilatura',
  },
];

// Desktop dropdown component
const NavDropdown = ({ item }: { item: NavItem }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!item.submenu) {
    return (
      <Link 
        to={item.href} 
        className="text-secondary-foreground font-semibold uppercase tracking-wide text-sm hover:text-primary transition-colors py-4"
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="flex items-center gap-1 text-secondary-foreground font-semibold uppercase tracking-wide text-sm hover:text-primary transition-colors py-4">
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 bg-primary shadow-lg rounded-lg py-2 min-w-48 z-50 animate-fade-in">
          {item.submenu.map((subItem) => (
            <Link
              key={subItem.href}
              to={subItem.href}
              className="block px-4 py-2 text-secondary-foreground font-medium hover:bg-primary/80 hover:text-primary-foreground transition-colors"
            >
              {subItem.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobile menu item with collapsible submenu
const MobileNavItem = ({ 
  item, 
  onNavigate 
}: { 
  item: NavItem; 
  onNavigate: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!item.submenu) {
    return (
      <Link 
        to={item.href} 
        onClick={onNavigate}
        className="block py-3 px-4 text-foreground font-semibold uppercase tracking-wide text-sm hover:text-[hsl(var(--brand-magenta))] transition-colors border-b border-border/30"
      >
        {item.label}
      </Link>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-b border-border/30">
      <CollapsibleTrigger className="flex items-center justify-between w-full py-3 px-4 text-foreground font-semibold uppercase tracking-wide text-sm hover:text-[hsl(var(--brand-magenta))] transition-colors">
        {item.label}
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="bg-primary/20">
        {item.submenu.map((subItem) => (
          <Link
            key={subItem.href}
            to={subItem.href}
            onClick={onNavigate}
            className="block py-2.5 pl-8 pr-4 text-foreground font-medium text-sm hover:text-[hsl(var(--brand-magenta))] transition-colors"
          >
            {subItem.label}
          </Link>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Mobile navigation with push-down menu
const MobileNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigate = () => {
    setIsOpen(false);
  };

  return (
    <nav className="bg-secondary">
      {/* Hamburger toggle bar */}
      <div className="container mx-auto px-4">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="w-full py-3 flex items-center justify-between text-secondary-foreground"
          aria-label={isOpen ? "Chiudi menu" : "Apri menu"}
        >
          <span className="font-semibold uppercase tracking-wide text-sm">Menu</span>
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>
      
      {/* Push-down menu content */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out bg-secondary border-t border-border ${
          isOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="overflow-y-auto max-h-[70vh]">
          {navItems.map((item) => (
            <MobileNavItem 
              key={item.label} 
              item={item} 
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </div>
    </nav>
  );
};

// Desktop navigation
const DesktopNavigation = () => {
  return (
    <nav className="bg-secondary">
      <div className="container mx-auto">
        <ul className="flex justify-center items-center gap-8">
          {navItems.map((item) => (
            <li key={item.label}>
              <NavDropdown item={item} />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export const Navigation = () => {
  const isMobile = useIsMobile();

  return isMobile ? <MobileNavigation /> : <DesktopNavigation />;
};

export default Navigation;
